import React from "react";
import { Link } from "react-router-dom";
import {
  Network, Landmark, Pickaxe, Compass, Leaf, Waves, TreePine, Wheat,
  Anchor, Bolt, Cable, Building2, ChevronRight, Sparkles,
} from "lucide-react";

/**
 * THOM360 Enterprise Platform Architecture — presentation-only positioning.
 * Communicates that Cadastre360 sits within a broader family of sovereign
 * modules WITHOUT splitting deployments, databases or renaming anything.
 */

const ACTIVE = "#C99A2E";
const RESERVED = "#5A7A8A";
const EXEC = "#7B2FBE";
const ENV = "#2E7D6B";

const PILLARS = [
  { key: "Land360",       label: "Land360™",         icon: Landmark, status: "Active",    color: ACTIVE, to: "/app/land",         desc: "National land cadastre, parcels, registry, titles, disputes, revenue." },
  { key: "Mine360",       label: "Mine360™",         icon: Pickaxe,  status: "Active",    color: ACTIVE, to: "/app/mining",       desc: "Mineral rights, licensing, inspections, royalties, investor portal." },
  { key: "Survey360",     label: "Survey360™",       icon: Compass,  status: "Active",    color: ACTIVE, to: "/app/survey",       desc: "Survey administration, geodesy, cadastral plans." },
  { key: "Environment360",label: "Environment360™",  icon: Leaf,     status: "Active",    color: ENV,    to: "/app/environment",  desc: "EIA & authorisations, permits, compliance, protected areas." },
  { key: "GSROS",         label: "GSROS™",           icon: Building2,status: "Active",    color: EXEC,   to: "/app/gsros",        desc: "Government Sovereign Resource Operating System — executive orchestration." },
  { key: "Water360",      label: "Water360™",        icon: Waves,    status: "Reserved",  color: RESERVED, to: "/app/thom360",     desc: "Reserved for future water governance expansion." },
  { key: "Forestry360",   label: "Forestry360™",     icon: TreePine, status: "Reserved",  color: RESERVED, to: "/app/thom360",     desc: "Reserved for future forestry governance expansion." },
  { key: "Agriculture360",label: "Agriculture360™",  icon: Wheat,    status: "Reserved",  color: RESERVED, to: "/app/thom360",     desc: "Reserved for agriculture and rangelands governance." },
  { key: "Marine360",     label: "Marine360™",       icon: Anchor,   status: "Reserved",  color: RESERVED, to: "/app/thom360",     desc: "Reserved for marine and coastal governance." },
  { key: "Energy360",     label: "Energy360™",       icon: Bolt,     status: "Reserved",  color: RESERVED, to: "/app/thom360",     desc: "Reserved for energy resources governance." },
  { key: "Infrastructure360", label: "Infrastructure360™", icon: Cable, status: "Reserved", color: RESERVED, to: "/app/thom360", desc: "Reserved for infrastructure governance." },
];

export const ThomArchitecture = () => {
  return (
    <div className="p-6 space-y-4" data-testid="page-thom360">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: EXEC + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${EXEC}25 100%)` }}>
        <Network size={38} className="mt-1" style={{ color: ACTIVE }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: ACTIVE }}>
            Cadastre360™ · Phase XII · THOM360 Enterprise Architecture
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            National Natural Resource Governance Platform
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            THOM360™ is the enterprise architecture within which Cadastre360 lives. Existing pillars
            (Land, Mining, Survey, Environment, GSROS) are shipped and operational. Future modules
            appear as <span className="italic">Reserved</span> placeholders — no deployment split,
            no database split, no rename. This is architectural positioning only.
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-white/10 p-4" style={{ background: "rgba(11,53,88,0.35)" }}>
        <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#C99A2E]">Architecture map</div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="thom360-pillars">
          {PILLARS.map((p) => (
            <Link key={p.key} to={p.to}
                  data-testid={"thom360-pillar-" + p.key}
                  className="rounded-sm border p-3 flex items-start gap-3"
                  style={{
                    borderColor: p.color + "40",
                    background: p.status === "Active"
                      ? "rgba(7,27,43,0.55)"
                      : "rgba(90,122,138,0.10)",
                    opacity: p.status === "Reserved" ? 0.85 : 1,
                  }}>
              <p.icon size={22} style={{ color: p.color }} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-[#F6F8FA] text-[15px]">{p.label}</div>
                  <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] ml-auto"
                        style={{ background: p.color + "25", color: p.color }}>
                    {p.status}
                  </span>
                </div>
                <div className="text-[11.5px] text-[#DCE3E8] mt-1 leading-relaxed">{p.desc}</div>
                {p.status === "Active" && (
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em] mt-1.5 inline-flex items-center gap-1"
                       style={{ color: p.color }}>
                    Open <ChevronRight size={10} />
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-sm border border-white/10 p-4" style={{ background: "rgba(11,53,88,0.25)" }}>
        <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#C99A2E]">Deployment note</div>
        <div className="mt-2 text-[12px] text-[#DCE3E8] leading-relaxed max-w-4xl">
          Cadastre360™ remains a single, unified sovereign deployment. THOM360 architecture prepares
          the platform for optional independent module deployment in the future — but the current
          production system is <span className="italic">not</span> split, and no database, route,
          API, workflow, permission or user is affected by this positioning.
        </div>
      </div>
    </div>
  );
};

export default ThomArchitecture;
